
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { HAS_FREE_PLAN } from '@/constants/common';
import HailuoSvg from '~/svg/Hailuo.svg';
const ACCENT = '#67e8f9';
const SHOWCASE_CDN = 'https://media.easy-peasy.ai/landing/minimax-h3';
const GENERATOR_URL = '/ai-videos?model=MiniMax%20H3';
const CheckIcon = () => (<svg className='h-4 w-4 flex-shrink-0 text-cyan-300' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
  </svg>);
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='mb-4 inline-block text-xs font-bold uppercase tracking-[0.2em]' style={{ color: ACCENT }}>
    {children}
  </span>);
type VideoExample = {
    src: string;
    poster: string;
    label: string;
    description: string;
    portrait?: boolean;
};
/**
 * Official MiniMax H3 outputs from MiniMax's launch release
 * (minimax.io/blog/minimax-h3), published 2026-07-31. The launch masters were
 * downloaded from MiniMax's own filecdn.minimax.chat URLs, resized to a
 * 1280px maximum edge, encoded as H.264 CRF 28 + AAC 96k with faststart, and
 * re-hosted in the R2 `images` bucket under `landing/minimax-h3/` with poster
 * frames. Stereo audio is deliberately preserved so visitors can hear the
 * model's native output after pressing play.
 */
const videoExamples: VideoExample[] = [
    {
        src: `${SHOWCASE_CDN}/multimodal-result.mp4`,
        poster: `${SHOWCASE_CDN}/multimodal-result-poster.jpg`,
        label: 'Full-Modality Direction',
        description: 'A camera move from video, a character from an image, and a voice from audio — combined in one prompt',
    },
    {
        src: `${SHOWCASE_CDN}/stereo-demo.mp4`,
        poster: `${SHOWCASE_CDN}/stereo-demo-poster.jpg`,
        label: 'Native Stereo Sound',
        description: 'Dialogue, effects, ambience, and spatial placement generated with the picture',
    },
    {
        src: `${SHOWCASE_CDN}/movie-intro.mp4`,
        poster: `${SHOWCASE_CDN}/movie-intro-poster.jpg`,
        label: 'Film Opening Titles',
        description: 'A complete cinematic title sequence with coherent art direction and typography',
    },
    {
        src: `${SHOWCASE_CDN}/game-ui.mp4`,
        poster: `${SHOWCASE_CDN}/game-ui-poster.jpg`,
        label: 'Product & Game UI',
        description: 'Interface motion, branded details, and scene transitions held together across a full take',
    },
    {
        src: `${SHOWCASE_CDN}/dynamic-poster.mp4`,
        poster: `${SHOWCASE_CDN}/dynamic-poster-poster.jpg`,
        label: 'Animated Poster',
        description: 'Portrait-format graphic design turned into a controlled motion piece',
        portrait: true,
    },
    {
        src: `${SHOWCASE_CDN}/ad-ecommerce.mp4`,
        poster: `${SHOWCASE_CDN}/ad-ecommerce-poster.jpg`,
        label: 'Advertising & E-commerce',
        description: 'A vertical commercial with product staging, readable brand treatment, and native sound',
        portrait: true,
    },
];
const VideoShowcase = ({ items, t, }: {
    items: VideoExample[];
    t: (key: string) => string;
}) => {
    const [failed, setFailed] = useState<string[]>([]);
    const visible = items.filter((item) => !failed.includes(item.src));
    if (visible.length === 0)
        return null;
    return (<section className='border-t border-white/[0.04] py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto mb-16 max-w-2xl text-center'>
          <SectionLabel>{t('Official Video Examples')}</SectionLabel>
          <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
            {t('See MiniMax H3 in Action')}
          </h2>
          <p className='mt-6 text-lg leading-relaxed text-gray-400'>
            {t('Every clip below comes from the official MiniMax H3 release. Press play with sound to hear the native stereo output.')}
          </p>
          <a href='https://www.minimax.io/blog/minimax-h3' target='_blank' rel='noreferrer' className='mt-5 inline-flex text-sm font-medium text-cyan-300 underline decoration-cyan-300/30 underline-offset-4 transition-colors hover:text-cyan-200'>
            {t('View the official MiniMax launch release ↗')}
          </a>
        </div>

        <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          {visible.map((video) => (<article key={video.src} className='group relative'>
              <div className='relative overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.02]'>
                <video src={video.src} poster={video.poster} controls playsInline preload='none' className={`aspect-video w-full ${video.portrait
                ? 'bg-black/40 object-contain'
                : 'object-cover'}`} onError={() => setFailed((current) => current.includes(video.src)
                ? current
                : [...current, video.src])}>
                  {t('Your browser does not support HTML video.')}
                </video>
              </div>
              <div className='mt-3 px-1'>
                <h3 className='text-sm font-semibold text-white'>
                  {t(video.label)}
                </h3>
                <p className='mt-1 text-xs leading-relaxed text-gray-500'>
                  {t(video.description)}
                </p>
              </div>
            </article>))}
        </div>
      </div>
    </section>);
};
export default function MiniMaxH3LandingPage() {
    const { t } = useLocale('minimax-h3');
    const creationModes = [
        {
            tag: 'T2V',
            title: t('Text to Video'),
            description: t('Describe the scene, action, camera, dialogue, and sound. H3 turns the prompt into a coherent clip with native stereo audio already inside it.'),
            points: [
                t('4–15 seconds at 768P or 2K'),
                t('Six landscape, square, and vertical aspect ratios'),
                t('Native dialogue, effects, music, and ambience'),
            ],
        },
        {
            tag: 'I2V',
            title: t('Image to Video'),
            description: t('Animate a still as the first frame, or upload both a first and last frame to control where the shot begins and exactly where it lands.'),
            points: [
                t('First-frame animation from any supported image'),
                t('Optional last frame for controlled transitions'),
                t('Output framing follows the source automatically'),
            ],
        },
        {
            tag: 'R2V',
            title: t('Reference to Video'),
            description: t('Combine character, product, and style images with reference audio, then explain each file’s role in natural language. H3 resolves the relationships in one generation.'),
            points: [
                t('Up to 9 reference images'),
                t('Up to 3 reference audio clips, 15 seconds total'),
                t('Natural-language roles for every uploaded reference'),
            ],
        },
    ];
    const referenceInputs = [
        {
            value: '9',
            label: t('Reference images'),
            detail: t('Characters, products, locations, style, and composition'),
        },
        {
            value: '3',
            label: t('Reference audio clips'),
            detail: t('Voice timbre, singing, music, and sound direction'),
        },
    ];
    const howItWorks = [
        {
            step: '01',
            title: t('Choose an H3 mode'),
            description: t('Open the AI Video Generator and select MiniMax H3, MiniMax H3 Image, or MiniMax H3 Reference depending on what you want to direct.'),
        },
        {
            step: '02',
            title: t('Add your context'),
            description: t('Start from text, a first frame, first and last frames, or a mix of reference images and audio. Ordinary photos of real people are accepted.'),
        },
        {
            step: '03',
            title: t('Direct the relationships'),
            description: t('Tell H3 what to borrow from each input: a face from Image 1, a product from Image 2, or voice timbre from Audio 1. Natural language connects it all.'),
        },
        {
            step: '04',
            title: t('Set the finish and render'),
            description: t('Choose any whole-second duration from 4 to 15 and render at efficient 768P or detail-first 2K. Stereo audio is included automatically.'),
        },
    ];
    const promptTips = [
        {
            title: t('Give each reference one clear job'),
            description: t('H3 understands relationships between files, but the relationship still needs to be explicit. Name the source and say exactly what should transfer.'),
            example: t('Use Image 1 for the presenter’s face and clothing. Keep the product from Image 2 unchanged in every shot. Match the speaking voice to Audio 1, but generate new words from the dialogue below.'),
        },
        {
            title: t('Direct sound as part of the shot'),
            description: t('Audio is not an afterthought. Describe dialogue, foley, music, ambience, and where they sit in the stereo field alongside the visual action.'),
            example: t('She says “we made it” under her breath. Wind moves from left to right as the camera circles. A low engine hum stays centred; no music until the final two seconds.'),
        },
        {
            title: t('Use the last frame as a destination'),
            description: t('A last frame works best when the prompt explains the journey between the two images instead of merely describing the destination again.'),
            example: t('Begin on the empty desk in the first frame. Components assemble in precise mechanical steps as the camera pushes closer, ending on the completed watch in the supplied last frame.'),
        },
        {
            title: t('Write visible text exactly'),
            description: t('H3 was released with a focus on text and brand rendering. Put required words in quotation marks, state where they appear, and keep the layout direction simple.'),
            example: t('A clean product end card. The headline reads “MOVE LIGHTER” in large white condensed type, centred above the shoe. The brand mark from Image 2 remains unchanged in the lower-right corner.'),
        },
    ];
    const specs = [
        {
            label: t('Maximum duration'),
            value: '15s',
            detail: t('Any whole second from 4–15'),
        },
        {
            label: t('Resolution'),
            value: '2K',
            detail: t('768P or 2K output tiers'),
        },
        {
            label: t('Audio'),
            value: t('Stereo'),
            detail: t('Generated natively with every clip'),
        },
        {
            label: t('Aspect ratios'),
            value: '6',
            detail: '21:9 · 16:9 · 4:3 · 1:1 · 3:4 · 9:16',
        },
        {
            label: t('Prompt length'),
            value: '4,000',
            detail: t('Characters in the Easy-Peasy generator'),
        },
        {
            label: t('Reference images'),
            value: '9',
            detail: t('JPG, PNG, WEBP, HEIC, and HEIF'),
        },
        {
            label: t('Reference audio'),
            value: '3',
            detail: t('WAV or MP3, 15 seconds combined'),
        },
        {
            label: t('First & last frame'),
            value: t('Yes'),
            detail: t('Controlled start and destination'),
        },
        {
            label: t('Real-person inputs'),
            value: t('Accepted'),
            detail: t('No rejection merely for containing a face'),
        },
        {
            label: t('768P price'),
            value: '3 cr/s',
            detail: t('12 credits for the default 4 seconds'),
        },
        {
            label: t('2K price'),
            value: '5 cr/s',
            detail: t('20 credits for 4 seconds'),
        },
    ];
    const comparison = [
        {
            feature: t('Maximum duration'),
            h3: t('4–15 seconds'),
            previous: t('6 or 10 seconds'),
        },
        {
            feature: t('Maximum resolution'),
            h3: '2K',
            previous: '1080P',
        },
        {
            feature: t('Native audio'),
            h3: t('Stereo audio on every render'),
            previous: t('Silent video output'),
        },
        {
            feature: t('Input context'),
            h3: t('Text + image + reference audio'),
            previous: t('Text or first-frame image'),
        },
        {
            feature: t('Reference control'),
            h3: t('Subject, motion, style, and voice in one model'),
            previous: t('Separate text and image workflows'),
        },
        {
            feature: t('First & last frames'),
            h3: t('Supported'),
            previous: t('Limited to older dedicated workflows'),
        },
        {
            feature: t('Output framing'),
            h3: t('Six aspect ratios plus adaptive reference mode'),
            previous: t('16:9 in the Easy-Peasy catalogue'),
        },
    ];
    const useCases = [
        {
            icon: '📣',
            title: t('Advertising'),
            description: t('Build complete vertical or landscape spots with product action, readable messaging, dialogue, effects, and a finished soundtrack.'),
        },
        {
            icon: '🛍️',
            title: t('E-commerce'),
            description: t('Keep the product anchored with image references while the camera, environment, and sound turn a still catalogue shot into a launch asset.'),
        },
        {
            icon: '🎮',
            title: t('Game & Product UI'),
            description: t('Animate interfaces, title screens, and product interactions while preserving the visual language supplied in your references.'),
        },
        {
            icon: '🎞️',
            title: t('Film Titles & Pre-vis'),
            description: t('Prototype opening sequences, camera paths, and scene transitions in one coherent take before production begins.'),
        },
        {
            icon: '🎤',
            title: t('Voice-Led Characters'),
            description: t('Use a person or character image with reference audio to guide voice timbre, singing, delivery, and synchronized performance.'),
        },
        {
            icon: '📱',
            title: t('Animated Posters'),
            description: t('Turn campaign art into a vertical motion piece with controlled typography, depth, particles, music, and stereo sound design.'),
        },
    ];
    const faqs = [
        {
            question: t('What is MiniMax H3?'),
            answer: t('MiniMax H3 is a general-purpose multimodal generation model released by MiniMax on July 31, 2026. It understands text, images, video, and audio together and generates up to 15 seconds of 2K video with native stereo sound. MiniMax designed it for controlled commercial content across advertising, branding, e-commerce, UI/UX, gaming, and more.'),
        },
        {
            question: t('What does “unrestricted” mean for MiniMax H3?'),
            answer: t('It means Easy-Peasy.AI does not reject an otherwise ordinary image merely because it contains a recognisable real person. You can animate your own portrait, creator photos, team headshots, and customer-approved material. You do not need a special toggle or a separate workflow.'),
        },
        {
            question: t('Does unrestricted mean there are no safety rules?'),
            answer: t('No. Unrestricted refers specifically to ordinary real-person inputs, not to prohibited content. You must have the right and consent to use uploaded likenesses. Deceptive impersonation, unlawful content, sexual content involving real people or minors, and other uses barred by our Terms of Use remain prohibited. MiniMax safety filters also still apply.'),
        },
        {
            question: t('Does MiniMax H3 generate audio?'),
            answer: t('Yes. Every H3 render includes native stereo audio generated together with the picture. The model jointly handles speech, sound effects, music, and ambience instead of attaching a separate soundtrack after the video is complete. H3 does not expose a silent-generation switch, but you can mute or replace the audio later in the editor.'),
        },
        {
            question: t('How long can a MiniMax H3 video be?'),
            answer: t('Choose any whole number of seconds from 4 to 15. Both 768P and 2K tiers support the full duration range. Longer stories can be built from multiple H3 shots in the Easy-Peasy.AI video editor.'),
        },
        {
            question: t('What can I use as reference material?'),
            answer: t('H3 Reference on Easy-Peasy.AI accepts up to 9 reference images and 3 reference audio clips. Audio clips must each be 2–15 seconds long and can total no more than 15 seconds. Reference audio must be paired with at least one reference image.'),
        },
        {
            question: t('Can MiniMax H3 use first and last frames?'),
            answer: t('Yes. Upload a first frame to animate a still, then optionally add a last frame to define the destination of the shot. First/last-frame mode and reference mode are separate: one generation cannot mix frame-control images with reference images or audio.'),
        },
        {
            question: t('How much does MiniMax H3 cost on Easy-Peasy.AI?'),
            answer: t('Pricing is per generated second. The 768P tier costs 3 credits per second and the 2K tier costs 5 credits per second. The default 4-second 768P render costs 12 credits. Native stereo audio and reference audio do not add a separate charge.'),
        },
        {
            question: t('Can I use MiniMax H3 videos commercially?'),
            answer: t('Yes. You can use videos generated on Easy-Peasy.AI for ads, social posts, product pages, client work, games, and other commercial projects, subject to our Terms of Use and your rights to any uploaded source material.'),
        },
    ];
    return (<main className='relative overflow-hidden bg-[#070d12]'>
      <Seo templateTitle={t('Unrestricted MiniMax H3 AI Video Generator | 2K, Native Stereo, Real Faces Allowed')} description={t('Create up to 15-second 2K videos with MiniMax H3 using text, images, and reference audio. Native stereo sound. Real-person inputs accepted.')} image={`${SHOWCASE_CDN}/2k-demo-poster.jpg`}/>

      {/* Hero */}
      <div className='relative min-h-[720px] max-h-[1000px] w-full overflow-hidden sm:min-h-[90vh]'>
        <div className='absolute inset-0'>
          <video autoPlay muted loop playsInline preload='metadata' poster={`${SHOWCASE_CDN}/2k-demo-poster.jpg`} src={`${SHOWCASE_CDN}/2k-demo.mp4`} className='absolute inset-0 h-full w-full object-cover'/>
          <div className='absolute inset-0 bg-[#070d12]/72'/>
          <div className='absolute inset-0 bg-gradient-to-b from-[#070d12]/35 via-transparent to-[#070d12]'/>
          <div className='absolute -left-32 top-0 h-[520px] w-[520px] rounded-full bg-cyan-400/[0.08] blur-[140px]'/>
          <div className='absolute -right-28 bottom-0 h-[500px] w-[500px] rounded-full bg-violet-500/[0.09] blur-[140px]'/>
          <div className='absolute left-[8%] top-0 h-full w-px bg-gradient-to-b from-transparent via-white/[0.06] to-transparent'/>
          <div className='absolute right-[8%] top-0 h-full w-px bg-gradient-to-b from-transparent via-white/[0.06] to-transparent'/>
        </div>

        <div className='absolute left-0 top-0 z-20 w-full'>
          <LandingPageNavigation variant='light'/>
        </div>

        <div className='relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-6 pt-36 sm:pt-48 lg:px-8 lg:pt-40'>
          <div className='mx-auto max-w-4xl text-center'>
            <div className='mb-10 flex justify-center'>
              <div className='inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-black/20 px-5 py-2 text-[13px] text-gray-300 backdrop-blur-sm'>
                <span className='flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white shadow-sm'>
                  <HailuoSvg className='h-5 w-5' aria-hidden='true'/>
                </span>
                {t("MiniMax's open multimodal video model")}
              </div>
            </div>

            <h1 className='text-5xl font-extrabold leading-[0.92] tracking-tight text-white sm:text-8xl'>
              <span className='mb-3 block text-2xl font-bold uppercase tracking-[0.15em] sm:text-4xl' style={{
            background: 'linear-gradient(135deg, #67e8f9 0%, #60a5fa 55%, #c084fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
                {t('Unrestricted')}
              </span>
              <span className='block'>MiniMax</span>
              <span className='mt-1 block' style={{
            background: 'linear-gradient(135deg, #67e8f9 0%, #60a5fa 50%, #c084fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
                H3
              </span>
            </h1>

            <p className='mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-gray-300 sm:text-xl'>
              {t('Direct with text, images, and reference audio. Generate up to 15 seconds at 2K with native stereo sound. Real-person photos are welcome.')}
            </p>

            <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
              <Link href={GENERATOR_URL} className='group inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-300 to-blue-400 px-8 py-4 text-base font-semibold text-[#061018] shadow-[0_12px_45px_-16px_rgba(103,232,249,0.9)] transition-all hover:scale-[1.03]'>
                {t('Create with MiniMax H3')}
                <span className='ml-2 transition-transform group-hover:translate-x-0.5'>
                  →
                </span>
              </Link>
              <a href='#unrestricted' className='text-sm font-medium text-gray-300 transition-colors hover:text-white'>
                {t('Why unrestricted?')}
              </a>
            </div>

            <div className='mt-8 flex justify-center'>
              <HappyUsers fromLandingPage={true} className='items-center'/>
            </div>

            <div className='mx-auto mt-14 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4'>
              {[
            { value: '15s', label: t('Maximum length') },
            { value: '2K', label: t('Resolution') },
            { value: t('Stereo'), label: t('Native audio') },
            { value: t('Yes'), label: t('Real people') },
        ].map((stat) => (<div key={stat.label} className='rounded-xl border border-white/[0.08] bg-black/20 px-3 py-4 backdrop-blur-sm'>
                  <div className='text-2xl font-bold text-cyan-300 sm:text-3xl'>
                    {stat.value}
                  </div>
                  <div className='mt-1 text-xs uppercase tracking-wider text-gray-400'>
                    {stat.label}
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </div>

      <LogosCloud />

      {/* Unrestricted */}
      <section id='unrestricted' className='border-y border-cyan-300/10 bg-cyan-300/[0.035] py-20 sm:py-24'>
        <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8'>
          <div>
            <SectionLabel>{t('Unrestricted on Easy-Peasy.AI')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Your Face Is an Input, Not a Rejection Reason')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-gray-400'>
              {t('Animate your portrait, a creator photo, a team headshot, or customer-approved material. Easy-Peasy.AI does not block an ordinary H3 job simply because a recognisable person appears in the input.')}
            </p>
            <p className='mt-4 text-sm leading-relaxed text-gray-500'>
              {t('Unrestricted does not mean unmoderated. Consent, likeness rights, our Terms of Use, and MiniMax safety filters still apply.')}
            </p>
          </div>

          <div className='grid gap-4 sm:grid-cols-2'>
            {[
            {
                title: t('Real portraits accepted'),
                text: t('Use selfies, headshots, creator photos, and team images you have permission to use.'),
            },
            {
                title: t('Creator images accepted'),
                text: t('Use approved creator and customer images as first frames or character references.'),
            },
            {
                title: t('One clear workflow'),
                text: t('There is no face-safe mode to find and no separate setting to enable before you generate.'),
            },
            {
                title: t('Your rights still matter'),
                text: t('Only upload likenesses and source material you own or are authorized to use.'),
            },
        ].map((item) => (<div key={item.title} className='rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6'>
                <div className='mb-4 flex h-8 w-8 items-center justify-center rounded-full bg-cyan-300/10'>
                  <CheckIcon />
                </div>
                <h3 className='font-semibold text-white'>{item.title}</h3>
                <p className='mt-2 text-sm leading-relaxed text-gray-500'>
                  {item.text}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* Creation modes */}
      <section className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Three Ways to Create')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('One Model, Every Starting Point')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-gray-400'>
              {t('Start with an idea, a frame, or a full set of multimodal references. H3 uses the same general-purpose model across all three workflows.')}
            </p>
          </div>

          <div className='grid gap-5 lg:grid-cols-3'>
            {creationModes.map((mode) => (<article key={mode.tag} className='rounded-2xl border border-white/[0.07] bg-white/[0.025] p-8 transition-colors hover:border-cyan-300/25'>
                <span className='inline-flex rounded-md border border-cyan-300/20 bg-cyan-300/[0.06] px-2.5 py-1 text-[10px] font-bold tracking-widest text-cyan-300'>
                  {mode.tag}
                </span>
                <h3 className='mt-5 text-xl font-bold text-white'>
                  {mode.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-gray-400'>
                  {mode.description}
                </p>
                <ul className='mt-6 space-y-3'>
                  {mode.points.map((point) => (<li key={point} className='flex items-start gap-2.5 text-sm text-gray-400'>
                      <CheckIcon />
                      <span>{point}</span>
                    </li>))}
                </ul>
              </article>))}
          </div>
        </div>
      </section>

      <VideoShowcase items={videoExamples} t={t}/>

      {/* Multimodal context */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:items-center lg:px-8'>
          <div>
            <SectionLabel>{t('Multimodal Context')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Tell H3 How the References Relate')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-gray-400'>
              {t('Most video tools treat character, product, style, and voice as separate jobs. H3 accepts them as one context. Describe the relationship in natural language and the model decides how to fuse them into the result.')}
            </p>
            <div className='mt-8 rounded-2xl border border-cyan-300/15 bg-cyan-300/[0.045] p-6'>
              <div className='text-[10px] font-bold uppercase tracking-widest text-cyan-300'>
                {t('Example direction')}
              </div>
              <p className='mt-3 text-sm italic leading-relaxed text-gray-300'>
                &ldquo;
                {t('Keep the person from Image 1 consistent, feature the product from Image 2, and match the singing voice to Audio 1.')}
                &rdquo;
              </p>
            </div>
          </div>

          <div className='space-y-4'>
            {referenceInputs.map((input, index) => (<div key={input.label} className='flex items-center gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6' style={{ marginLeft: `${index * 24}px` }}>
                <div className='flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-300/15 to-violet-400/10 text-2xl font-bold text-cyan-300'>
                  {input.value}
                </div>
                <div>
                  <h3 className='font-semibold text-white'>{input.label}</h3>
                  <p className='mt-1 text-sm leading-relaxed text-gray-500'>
                    {input.detail}
                  </p>
                </div>
              </div>))}
            <div className='ml-[72px] rounded-2xl border border-violet-400/15 bg-violet-400/[0.035] p-5 text-center text-sm text-violet-200'>
              {t('One natural-language prompt connects every input →')}
            </div>
          </div>
        </div>
      </section>

      {/* 2K and stereo */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-6 lg:grid-cols-2'>
            <article className='relative overflow-hidden rounded-3xl border border-white/[0.07] bg-white/[0.025] p-8 sm:p-10'>
              <div className='absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyan-300/[0.08] blur-[80px]'/>
              <SectionLabel>{t('Native 2K')}</SectionLabel>
              <h2 className='relative text-3xl font-bold tracking-tight text-white sm:text-4xl'>
                {t('Detail Regenerated, Not Merely Upscaled')}
              </h2>
              <p className='relative mt-5 leading-relaxed text-gray-400'>
                {t('H3 uses in-context regeneration for its 2K tier. The base model looks back at the original prompt and reference context while rebuilding detail—useful for small type, product texture, and brand elements that a conventional upscaler would have to guess.')}
              </p>
              <ul className='relative mt-7 space-y-3'>
                {[
            t('2K at every duration from 4–15 seconds'),
            t('768P tier for lower-cost iteration'),
            t('Six output ratios for ads, feeds, and widescreen work'),
        ].map((point) => (<li key={point} className='flex items-start gap-2.5 text-sm text-gray-400'>
                    <CheckIcon />
                    <span>{point}</span>
                  </li>))}
              </ul>
            </article>

            <article className='relative overflow-hidden rounded-3xl border border-white/[0.07] bg-white/[0.025] p-8 sm:p-10'>
              <div className='absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-violet-400/[0.09] blur-[80px]'/>
              <SectionLabel>{t('Native Stereo')}</SectionLabel>
              <h2 className='relative text-3xl font-bold tracking-tight text-white sm:text-4xl'>
                {t('The Soundtrack Is Part of the Generation')}
              </h2>
              <p className='relative mt-5 leading-relaxed text-gray-400'>
                {t('Speech, effects, music, and ambience are jointly modeled with the visual sequence. That gives H3 a shared timeline for mouth movement, impacts, environmental sound, and camera motion—and a real left/right stereo field.')}
              </p>
              <ul className='relative mt-7 space-y-3'>
                {[
            t('Dialogue and singing generated in-scene'),
            t('Action-matched foley and environmental sound'),
            t('Reference audio can guide voice timbre and music'),
        ].map((point) => (<li key={point} className='flex items-start gap-2.5 text-sm text-gray-400'>
                    <CheckIcon />
                    <span>{point}</span>
                  </li>))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-20 max-w-2xl text-center'>
            <SectionLabel>{t('Workflow')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('How It Works')}
            </h2>
          </div>
          <div className='grid gap-8 sm:grid-cols-2 lg:grid-cols-4'>
            {howItWorks.map((step) => (<article key={step.step}>
                <div className='mb-4 text-5xl font-black text-cyan-300/15'>
                  {step.step}
                </div>
                <h3 className='mb-3 text-lg font-bold text-white'>
                  {step.title}
                </h3>
                <p className='text-sm leading-relaxed text-gray-400'>
                  {step.description}
                </p>
              </article>))}
          </div>
        </div>
      </section>

      {/* Prompt guide */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-20 max-w-2xl text-center'>
            <SectionLabel>{t('Prompt Guide')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Prompt the Context, Not Just the Scene')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-gray-400'>
              {t('The best H3 prompts explain what to create and what role each source should play. These four habits make multimodal direction much more predictable.')}
            </p>
          </div>
          <div className='grid gap-6 lg:grid-cols-2'>
            {promptTips.map((tip) => (<article key={tip.title} className='rounded-2xl border border-white/[0.07] bg-white/[0.025] p-8'>
                <h3 className='text-lg font-bold text-white'>{tip.title}</h3>
                <p className='mt-3 text-sm leading-relaxed text-gray-400'>
                  {tip.description}
                </p>
                <div className='mt-5 rounded-xl border border-white/[0.06] bg-black/20 p-4'>
                  <div className='mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600'>
                    {t('Example prompt')}
                  </div>
                  <p className='text-sm italic leading-relaxed text-gray-300'>
                    &ldquo;{tip.example}&rdquo;
                  </p>
                </div>
              </article>))}
          </div>
        </div>
      </section>

      {/* Specs */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Specifications')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('MiniMax H3 Technical Details')}
            </h2>
          </div>
          <div className='mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
            {specs.map((spec) => (<div key={spec.label} className='rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5'>
                <div className='text-xs uppercase tracking-wider text-gray-500'>
                  {spec.label}
                </div>
                <div className='mt-2 text-2xl font-bold text-cyan-300'>
                  {spec.value}
                </div>
                <div className='mt-1 text-xs leading-relaxed text-gray-500'>
                  {spec.detail}
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Generation Upgrade')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('MiniMax H3 vs Hailuo 2.3')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-gray-400'>
              {t('H3 moves beyond separate text and image generators into one multimodal system—with longer clips, native stereo, reference control, and 2K output.')}
            </p>
          </div>
          <div className='mx-auto max-w-4xl overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='border-b border-white/[0.08]'>
                  <th className='px-4 py-4 text-left text-sm font-medium text-gray-500'>
                    {t('Feature')}
                  </th>
                  <th className='px-4 py-4 text-left text-sm font-bold text-cyan-300'>
                    MiniMax H3
                  </th>
                  <th className='px-4 py-4 text-left text-sm font-medium text-gray-600'>
                    Hailuo 2.3
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((item, index) => (<tr key={item.feature} className={index % 2 === 0 ? 'bg-white/[0.01]' : ''}>
                    <td className='px-4 py-3.5 text-sm font-medium text-gray-300'>
                      {item.feature}
                    </td>
                    <td className='px-4 py-3.5 text-sm text-gray-200'>
                      <span className='inline-flex items-start gap-2'>
                        <CheckIcon />
                        {item.h3}
                      </span>
                    </td>
                    <td className='px-4 py-3.5 text-sm text-gray-600'>
                      {item.previous}
                    </td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Commercial-Ready Use Cases')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('What You Can Create')}
            </h2>
          </div>
          <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
            {useCases.map((useCase) => (<article key={useCase.title} className='rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6 transition-colors hover:border-cyan-300/25'>
                <span className='text-2xl' aria-hidden='true'>
                  {useCase.icon}
                </span>
                <h3 className='mt-3 font-bold text-white'>{useCase.title}</h3>
                <p className='mt-2 text-sm leading-relaxed text-gray-400'>
                  {useCase.description}
                </p>
              </article>))}
          </div>
          <div className='mt-16 flex justify-center'>
            <ButtonLink href={HAS_FREE_PLAN ? GENERATOR_URL : '/#pricing'} variant='dark' className='px-8 py-3 text-lg'>
              {t('Create with MiniMax H3')}
            </ButtonLink>
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      {/* FAQ */}
      <section className='border-t border-white/[0.04] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('FAQ')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-5xl'>
              {t('Frequently Asked Questions')}
            </h2>
            <p className='mt-4 text-lg text-gray-400'>
              {t('Everything you need to know about MiniMax H3')}
            </p>
          </div>
          <div className='mx-auto max-w-3xl space-y-4'>
            {faqs.map((faq) => (<details key={faq.question} className='group rounded-xl border border-white/[0.07] bg-white/[0.025]'>
                <summary className='flex cursor-pointer items-center justify-between p-5 text-base font-semibold text-white'>
                  {faq.question}
                  <svg className='ml-4 h-5 w-5 flex-shrink-0 text-gray-600 transition-transform group-open:rotate-180' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7'/>
                  </svg>
                </summary>
                <div className='px-5 pb-5'>
                  <p className='text-sm leading-relaxed text-gray-400'>
                    {faq.answer}
                  </p>
                </div>
              </details>))}
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-videos'/>
        <Footer />
      </div>
    </main>);
}
const getStaticProps: GetStaticProps = async () => ({
    props: {},
});

    async function __Next_Translate__getStaticProps__1a0cbfb65c9__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/minimax-h3',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb65c9__ as getStaticProps }
  